import { Layout } from 'antd';
import { useAppDispatch } from 'app/hooks';
import classNames from 'classnames';
import RevxLogoText from 'components/RevxLogoText';
import { OrderHistoryItem } from 'domains/orderDomain';
import {
  JobItem,
  JobWorkflowStatusEnum,
  JobWorkspaceFilterTypeEnum,
} from 'domains/workspaceDomain';
import { RevxJobInfiniteList } from 'components/RevxJobInfiniteList';
import { useUser } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isBuyer, isSupplier } from 'utils/rbac';
import UniversalWorkspaceItem from '../Workspace/list/UniversalWorkspaceItem';
import BuyerOrderHistoryFilter, {
  BuyerOrderHistoryFilterParams,
} from './components/BuyerOrderHistoryFilter';
import { OrderHistoryItemContainer } from './components/OrderHistoryItemContainer';
import { OrderInfiniteList } from './components/OrderInfiniteList';
import RevxJobListFilter, {
  FilterOptionsParams,
} from 'components/RevxJobListFilter';
import { OrderHistorySearchGroupEnum } from 'domains/orderDomain';
import { useLocateOrderHistoryJob } from './useLocateOrderHistoryJob';
import './OrderHistoryLayout.less';

const { MY_ORDERS, ORGANIZATION_ORDERS } = OrderHistorySearchGroupEnum;
const { JOB_WORKSPACE, ORDER_HISTORY } = JobWorkspaceFilterTypeEnum;

export enum orderHistoryTabsKey {
  ORDER = 1,
  ORG_ORDER = 2,
  JOB = 3,
}

const { ORDER, ORG_ORDER } = orderHistoryTabsKey;

const { Content } = Layout;

const ShoppingCartLayout = () => {
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  // const items = useAppSelector((state) => state.order.orderHistory);
  // deep link from elsewhere in the app, for instance the closed RFM board
  const locateJobId = useMemo(() => {
    return new URLSearchParams(search).get('job_id') || undefined;
  }, [search]);
  // a job reached by deep link is rarely one of the sourcer's own orders
  const [selectTabKey, setSelectTabKey] = useState<number>(
    locateJobId ? ORG_ORDER : ORDER
  );
  const [totalElements, setTotalElements] = useState<number>(0);
  const [currentPageCount, setCurrentPageCount] = useState<number>(0);
  const [filterOptions, setFilterOptions] = useState<
    FilterOptionsParams & BuyerOrderHistoryFilterParams
  >();
  const [isOrderList, setIsOrderList] = useState<boolean>(true);
  const user = useUser();
  const buyer = useMemo(() => isBuyer(user), [user]);
  const orderId = useMemo(() => {
    return new URLSearchParams(search).get('orderId') || undefined;
  }, [search]);
  const isWaitingForInitialBuyerFilter = buyer && !!orderId && !filterOptions;

  useLocateOrderHistoryJob(locateJobId);

  useEffect(() => {
    if (filterOptions) {
      setCurrentPageCount(0);
      if (selectTabKey === ORDER || selectTabKey === ORG_ORDER) {
        dispatch.order.resetOrder();
      } else {
        dispatch.workspace.reloadJobList();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  const supplier = useMemo(() => isSupplier(user), [user]);

  const tabsData = [
    {
      name: 'My Orders',
      key: ORDER,
      showSupplier: false,
    },
    {
      name: 'Organization’s Orders',
      key: ORG_ORDER,
      showSupplier: false,
    },
    /*{
      name: 'Jobs Produced',
      key: JOB,
      showSupplier: true,
    },*/
  ];

  const filter = JobWorkflowStatusEnum.COMPLETED;

  const itemRender = ({ items }: { items?: JobItem[] | undefined }) => {
    return (
      <>
        {(items || []).map((item: JobItem) => {
          return <UniversalWorkspaceItem data={item} key={item.id} />;
        })}
      </>
    );
  };

  const searchGroupValue = useMemo(() => {
    setFilterOptions(undefined);
    return selectTabKey === ORDER ? MY_ORDERS : ORGANIZATION_ORDERS;
  }, [selectTabKey]);

  const historyRender = ({
    items,
  }: {
    items?: OrderHistoryItem[] | undefined;
  }) => {
    if (!items || !items.length) {
      return <></>;
    }
    return (
      <>
        {(items || []).map((item: OrderHistoryItem) => {
          return (
            <OrderHistoryItemContainer
              data={item}
              orderType={selectTabKey}
              key={item.id}
            />
          );
        })}
      </>
    );
  };

  return (
    <Layout className="order-history-layout">
      <div className="order-history-header">
        <RevxLogoText
          text="Order History"
          colors={[
            'blue',
            'red',
            'yellow',
            'black',
            'blue',
            '',
            'red',
            'yellow',
            'black',
            'blue',
            'red',
            'yellow',
            'black',
          ]}
        />
      </div>
      {buyer ? (
        <BuyerOrderHistoryFilter
          total={totalElements}
          displayTotal={currentPageCount}
          initialOrderId={orderId}
          searchGroup={searchGroupValue}
          filterType={ORDER_HISTORY}
          callback={(v) => {
            if (v) {
              setFilterOptions(Object.assign({}, filterOptions, v));
            }
          }}
        />
      ) : (
        <>
          <div className="order-history__tabs">
            {tabsData.map(({ name, key, showSupplier }) => {
              if (showSupplier && !supplier) {
                return null;
              }
              return (
                <div
                  className={classNames('order-history__tabs__items', {
                    active: key === selectTabKey,
                  })}
                  key={key}
                  onClick={() => {
                    setCurrentPageCount(0);
                    setSelectTabKey(key);
                    setIsOrderList(key === ORDER || key === ORG_ORDER);
                  }}
                >
                  {name}
                </div>
              );
            })}
          </div>
          <RevxJobListFilter
            total={totalElements}
            displayTotal={currentPageCount}
            isOrderList={isOrderList}
            statusFilter={filter}
            searchGroup={searchGroupValue}
            filterType={isOrderList ? ORDER_HISTORY : JOB_WORKSPACE}
            initialJobOrOrderNumber={locateJobId}
            callback={(v) => {
              if (v) {
                setFilterOptions(Object.assign({}, filterOptions, v));
              }
            }}
          />
        </>
      )}

      <Content className="order-history-content">
        <>
          {[ORDER, ORG_ORDER].includes(selectTabKey) ? (
            <div>
              {!isWaitingForInitialBuyerFilter && (
                <OrderInfiniteList
                  key={selectTabKey}
                  loadMore={async (page) => {
                    page = page || 1;
                    const listData = await dispatch.order.getHistoryList({
                      page,
                      ...filterOptions,
                      searchGroup: searchGroupValue,
                      orderId: buyer ? filterOptions?.orderId : undefined,
                    });
                    const { totalElements } = listData;
                    const pageCount = page * 10;
                    pageCount < totalElements
                      ? setCurrentPageCount((v) => pageCount)
                      : setCurrentPageCount(totalElements);
                    setTotalElements(totalElements);
                    return listData;
                  }}
                  render={historyRender}
                />
              )}
            </div>
          ) : (
            <>
              {supplier && (
                <div className="order-history__job-content">
                  <RevxJobInfiniteList
                    loadMore={async (page) => {
                      const listData = await dispatch.workspace.getPageJobList({
                        page,
                        filter,
                        ...filterOptions,
                        filterType: JOB_WORKSPACE,
                      });
                      setCurrentPageCount((v) => v + listData.content.length);
                      setTotalElements(listData.totalElements);
                      return listData;
                    }}
                    render={itemRender}
                    jobStatus={filter}
                  />
                </div>
              )}
            </>
          )}
        </>
      </Content>
    </Layout>
  );
};

export default ShoppingCartLayout;
